(ns capra.microbatch
  "Implicit batching for capra calls outside pipe!.

   Calls accumulate in a queue and flush on the next microtask.
   Independent calls batch into one round trip. Dependent calls
   (where target is a Promise) chain into subsequent batches."
  (:require
   [capra.context :as ctx]
   [capra.stub :as stub]
   [capra.protocol :as proto]
   [capra.pipeline :as pipeline]))

(defn make-microbatch-ctx
  "Create a fresh microbatch context. One per CapraClient."
  []
  {:pending-queue   (atom [])
   :flush-scheduled? (atom false)
   :qid-counter     (atom 0)})

(defn- flush-queue!
  "Flush all pending ops as a single pipeline."
  [ctx transport-send-fn!]
  (let [ops (let [q @(:pending-queue ctx)]
              (reset! (:pending-queue ctx) [])
              (reset! (:flush-scheduled? ctx) false)
              q)]
    (when (seq ops)
      (let [indexed-ops (map-indexed (fn [i op] (assoc op :batch-idx i)) ops)
            payload {:type   :rpc/call
                     :qid    (swap! (:qid-counter ctx) inc)
                     :ops    (mapv #(select-keys % [:op :args :cap]) ops)
                     :return (set (range (count ops)))}]
        (-> (transport-send-fn! payload)
            (.then (fn [response]
                     (let [{:keys [results caps]} response
                           cap-adverts (or caps [])]
                       (doseq [{:keys [batch-idx resolve]} indexed-ops]
                         (let [raw (get results batch-idx)]
                           (resolve
                            (pipeline/resolve-results
                             response
                             (ctx/->PendingRef batch-idx))))))))
            (.catch (fn [err]
                      (doseq [{:keys [reject]} ops]
                        (reject err)))))))))

(defn enqueue-call!
  "Add a call to the implicit batch queue. Returns a Promise.
   Schedules a microtask flush if not already scheduled."
  [ctx transport-send-fn! target method args]
  (js/Promise.
   (fn [resolve reject]
     (let [wire-target (cond
                         (nil? target)        nil
                         (= :root target)     nil
                         (stub/stub? target)  (stub/stub->wire target)
                         (proto/cap? target)  target
                         :else (throw (ex-info "Invalid target for implicit call"
                                               {:target target})))
           wire-args (ctx/normalize-args args)]
       (swap! (:pending-queue ctx) conj
              (cond-> {:op      method
                       :args    wire-args
                       :resolve resolve
                       :reject  reject}
                wire-target (assoc :cap wire-target)))
       (when-not @(:flush-scheduled? ctx)
         (reset! (:flush-scheduled? ctx) true)
         (js/queueMicrotask #(flush-queue! ctx transport-send-fn!)))))))

(defn next-qid
  "Get the next sequential qid from a microbatch context."
  [ctx]
  (swap! (:qid-counter ctx) inc))

(defn reset-state!
  "Reset microbatch state. Used on disconnect/reconnect."
  [ctx]
  (reset! (:pending-queue ctx) [])
  (reset! (:flush-scheduled? ctx) false))
